import { Icon } from "@mui/material";
import { get } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Container, Row } from "reactstrap";
import CollapsibleCard from "src/components/CollapsibleCard/CollapsibleCard";
import { IconName } from "src/components/Icon/Icon";
import Toast from "../../../components/Toast/Toast";
import * as S from "../../../constants/StringConstants";
import { IRootState } from "../../../store/reducers";
import { fetchBrokerDocuments } from "../../../store/reducers/brokerSecureUpload";
import "./BrokerSecureDocumentsWidget.scss";

interface RouteParams {
  id: string;
}

const buildPagesList = (page: number, numPages: number, displayMax: number = 10) => {
  let a = page - displayMax / 2;
  let b = page + displayMax / 2;
  while (a < 0) {
    a++;
    b++;
  }
  while (b > numPages) {
    a--;
    b--;
  }
  let arr = [];
  for (let i = a; i <= b; i++) {
    if (i > 0 && i <= numPages) {
      arr.push(i);
    }
  }
  return arr;
};

const BrokerSecureDocumentsWidget: React.FC = () => {
  const { id: brokerID } = useParams<RouteParams>();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [loading, setLoading] = useState<boolean>(false);

  const secureUploadDocuments = useSelector((state: IRootState) => state.brokerSecureUpload.documents);

  useEffect(() => {
    const parsedBrokerID = +get({ params: { id: brokerID } }, "params.id", "new");
    if (parsedBrokerID && typeof parsedBrokerID === "number") {
      dispatch(fetchBrokerDocuments(parsedBrokerID));
    }
  }, [dispatch, brokerID]);

  const handlePageChange = useCallback((pageNumber: number) => {
    setLoading(true);
    setPage(pageNumber);
    const timerId = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timerId);
  }, []);

  const total = secureUploadDocuments ? secureUploadDocuments.length : 0;
  const isPaginated = secureUploadDocuments ? Math.ceil(total / itemsPerPage) : 0;

  const rowStart = (page - 1) * itemsPerPage + 1;
  const rowEnd = Math.min(page * itemsPerPage, total);

  const currentFiles = secureUploadDocuments ? secureUploadDocuments.slice(rowStart - 1, rowEnd) : [];
  const pagesList = buildPagesList(page, isPaginated);

  return (
    <>
      <CollapsibleCard
        title={S.PD_BROKER_SECURE_DOCUMENTS}
        iconName={IconName.BrokerBuilderDocsUploaded}
        defaultOpen={true}>
        <Container>
          <Row className="card-row card-table-header card-file-list-header">
            <Col>{S.SU_BROKER_FILE_COLUMN}</Col>
            <Col>{S.SU_BROKER_TIME_COLUMN}</Col>
          </Row>
          <Row className="card-separator" />
          <div className="card-file-list-container">
            {currentFiles.length > 0 ? (
              currentFiles.map(x => (
                <Row className="card-documents" key={x.fileName}>
                  <Col className="card-documents-uploaded-list">{x.fileName}</Col>
                  <Col className="card-documents-upload-dates">{x.uploadDate}</Col>
                </Row>
              ))
            ) : (
              <Row className="card-row">
                <Col>{S.SU_BROKER_NO_FILES_UPLOADED}</Col>
              </Row>
            )}
          </div>
          <section className="card-footer">
            <div className="card-footer__container">
              <div className="card-footer__pagination-page">
                <div
                  className={page === 1 || total === 0 ? "disabled" : ""}
                  onClick={() => {
                    if (page > 1) handlePageChange(1);
                  }}>
                  <Icon>keyboard_double_arrow_left</Icon>
                </div>
                <div
                  className={page === 1 || total === 0 ? "disabled" : ""}
                  onClick={() => {
                    if (page > 1) handlePageChange(page - 1);
                  }}>
                  <Icon>chevron_left</Icon>
                </div>
                {pagesList.map(p => (
                  <div key={p} className={p === page ? "active" : ""} onClick={() => handlePageChange(p)}>
                    {p === page ? <strong>{p}</strong> : p}
                  </div>
                ))}
                <div
                  className={page === isPaginated || total === 0 ? "disabled" : ""}
                  onClick={() => {
                    if (page < isPaginated) handlePageChange(page + 1);
                  }}>
                  <Icon>chevron_right</Icon>
                </div>
                <div
                  className={page === isPaginated || total === 0 ? "disabled" : ""}
                  onClick={() => {
                    if (page < isPaginated) handlePageChange(isPaginated);
                  }}>
                  <Icon>keyboard_double_arrow_right</Icon>
                </div>
              </div>
            </div>
          </section>
        </Container>
      </CollapsibleCard>
      <Toast open={loading} message={S.LOADING} onClose={() => {}} />
    </>
  );
};

export default BrokerSecureDocumentsWidget;
